import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal } from "../common/Reveal";
import { clients, clientCategories } from "../../data/clients";

function Wordmark({ name }) {
  return (
    <div
      className="flex h-16 min-w-[180px] items-center justify-center rounded-xl border border-slate-200 bg-white px-6 shadow-[0_1px_3px_rgba(15,23,42,0.06)] transition-all duration-300 hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-[0_8px_24px_rgba(15,23,42,0.10)]"
    >
      <span
        className="whitespace-nowrap text-center font-heading text-base font-semibold tracking-tight text-slate-400 transition-colors duration-300 group-hover/card:text-slate-900"
        aria-label={`${name} logo`}
      >
        {name}
      </span>
    </div>
  );
}

function MarqueeRow({ items, direction = "left", duration = 55 }) {
  const doubled = [...items, ...items];
  return (
    <div className="marquee-row group overflow-hidden">
      <div
        className="marquee-track gap-4 py-2"
        style={{ animation: `amvi-marquee-${direction} ${duration}s linear infinite` }}
      >
        {doubled.map((c, i) => (
          <div key={`${c.name}-${i}`} className="group/card">
            <Wordmark name={c.name} />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function OrganizationsSection() {
  const [category, setCategory] = useState("All");
  const [showAll, setShowAll] = useState(false);

  const filtered = useMemo(
    () => (category === "All" ? clients : clients.filter((c) => c.category === category)),
    [category]
  );

  const rows = useMemo(() => {
    const size = Math.ceil(filtered.length / 3);
    return [filtered.slice(0, size), filtered.slice(size, size * 2), filtered.slice(size * 2)].filter(
      (r) => r.length > 0
    );
  }, [filtered]);

  return (
    <section className="relative bg-gradient-to-b from-white to-slate-50 px-6 py-24 lg:py-32" data-testid="organizations-section">
      <div className="mx-auto max-w-7xl">
        <Reveal className="mx-auto max-w-3xl text-center">
          <div className="mb-4 inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.25em] text-sky-600">
            <span className="h-px w-8 bg-sky-500/50" />
            Industries &amp; Organizations We Support
            <span className="h-px w-8 bg-sky-500/50" />
          </div>
          <h2 className="font-heading text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
            Trusted by Leading Organizations
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-slate-500 lg:text-lg">
            Connecting exceptional talent with organizations shaping the future of technology,
            healthcare, engineering, financial services, and aviation.
          </p>
        </Reveal>

        {/* Category filters */}
        <div className="mt-10 flex flex-wrap justify-center gap-2" data-testid="organizations-filters">
          {clientCategories.map((c) => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
                category === c
                  ? "border-slate-900 bg-slate-900 text-white"
                  : "border-slate-200 bg-white text-slate-600 hover:border-slate-400 hover:text-slate-900"
              }`}
              data-testid={`organizations-filter-${c.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
            >
              {c}
            </button>
          ))}
        </div>

        {/* Marquee rows */}
        <div className="relative mt-12">
          <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-16 bg-gradient-to-r from-slate-50 to-transparent sm:w-28" />
          <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-16 bg-gradient-to-l from-slate-50 to-transparent sm:w-28" />
          <AnimatePresence mode="wait">
            <motion.div
              key={category}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              className="space-y-4"
            >
              {rows.map((row, i) => (
                <MarqueeRow
                  key={i}
                  items={row}
                  direction={i % 2 === 0 ? "left" : "right"}
                  duration={50 + i * 12}
                />
              ))}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* View all */}
        <div className="mt-10 text-center">
          <button
            onClick={() => setShowAll((v) => !v)}
            className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-6 py-3 text-sm font-medium text-slate-800 transition-colors hover:border-slate-900 hover:text-slate-900"
            data-testid="organizations-view-all"
          >
            {showAll ? "Hide Full List" : "View All Organizations"}
          </button>
        </div>

        <AnimatePresence initial={false}>
          {showAll && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.4 }}
              className="overflow-hidden"
            >
              <div className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-testid="organizations-full-list">
                {filtered.map((c, i) => (
                  <motion.div
                    key={c.name}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.35, delay: Math.min(i * 0.015, 0.4) }}
                    className="group/card"
                  >
                    <div className="flex h-16 items-center justify-center rounded-xl border border-slate-200 bg-white px-4 text-center shadow-[0_1px_3px_rgba(15,23,42,0.06)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(15,23,42,0.10)]">
                      <span className="text-sm font-semibold tracking-tight text-slate-400 transition-colors duration-300 group-hover/card:text-slate-900" aria-label={`${c.name} logo`}>
                        {c.name}
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <p className="mt-10 text-center text-xs text-slate-400">
          Organizations shown represent sectors and companies AMVI Technologies supports and serves.
        </p>
      </div>
    </section>
  );
}
